/**
 * Troubleshoot content for the Printer Helper dialog (replaces the old standalone
 * PrinterTroubleshootDialog). Kept compact so it fits without scrolling:
 *   - no saved printer  → inline available-printers list (scan/select/connect)
 *   - saved printer      → a compact SDK status row + a one-tap Reconnect, then
 *                          optional retry-print. Bluetooth help is tucked behind
 *                          an expandable toggle.
 * The connection status lives in the dialog header, not here.
 */
import { useState } from 'react';
import { Capacitor } from '@capacitor/core';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import LoadingButton from '@mui/lab/LoadingButton';
import MuiCancelIcon from '@mui/icons-material/Cancel';
import MuiBluetoothIcon from '@mui/icons-material/Bluetooth';
import CircularProgress from '@mui/material/CircularProgress';
import MuiExpandLessIcon from '@mui/icons-material/ExpandLess';
import MuiExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiCheckCircleIcon from '@mui/icons-material/CheckCircle';

import { useBoolean } from 'src/hooks/use-boolean';
import { useBluetoothAvailability } from 'src/hooks/use-bluetooth-availability';

import { usePrinterStore } from 'src/store/PrinterStore';
import type { StarPrinterStatus } from 'src/services/printers';
import allowConnectionsImg from 'src/assets/images/allow-bluetooth-new-connections.png';

import Label from 'src/components/label';
import MuiIcon from 'src/components/mui-icon';

import BluetoothOffNotice from './bluetooth-off-notice';
import AvailablePrintersList from './available-printers-list';
import BluetoothControlCenterImage from './bluetooth-control-center-image';

// ----------------------------------------------------------------------

/** Small confirmation chip that the Bluetooth radio is on. */
function BluetoothOnChip() {
  return (
    <Box>
      <Label color="success" variant="soft" startIcon={<MuiIcon icon={MuiBluetoothIcon} />}>
        Bluetooth on
      </Label>
    </Box>
  );
}

interface PrinterTroubleshootViewProps {
  status: StarPrinterStatus | null;
  /** Called once a printer is (re)connected, so the dialog can return to status. */
  onConnected: () => void;
  pendingRetry: (() => Promise<boolean>) | null;
}

/** Compact pass/fail chip driven by the SDK status. */
function SdkChip({ label, ok }: { label: string; ok: boolean }) {
  return (
    <Label
      color={ok ? 'success' : 'error'}
      variant="soft"
      startIcon={<MuiIcon icon={ok ? MuiCheckCircleIcon : MuiCancelIcon} />}
    >
      {label}
    </Label>
  );
}

export default function PrinterTroubleshootView({
  status,
  onConnected,
  pendingRetry,
}: PrinterTroubleshootViewProps) {
  const activePrinter = usePrinterStore((s) => s.activePrinter);
  const resetConnection = usePrinterStore((s) => s.resetConnection);
  const { state: bluetoothState, recheck: recheckBluetooth } = useBluetoothAvailability();

  const switchPrinter = useBoolean(false);
  const bluetoothHelp = useBoolean(false);

  const [reconnecting, setReconnecting] = useState(false);
  const [reconnectStep, setReconnectStep] = useState<string | null>(null);
  const [reconnectResult, setReconnectResult] = useState<'success' | 'fail' | null>(null);
  const [reconnectError, setReconnectError] = useState<string | null>(null);

  const [retrying, setRetrying] = useState(false);
  const [retryResult, setRetryResult] = useState<'success' | 'fail' | null>(null);

  const handleReconnect = async () => {
    setReconnecting(true);
    setReconnectStep(null);
    setReconnectResult(null);
    setReconnectError(null);
    try {
      await resetConnection((step) => setReconnectStep(step));
      if (usePrinterStore.getState().isPrinterReady) {
        setReconnectResult('success');
      } else {
        setReconnectResult('fail');
        setReconnectError(
          usePrinterStore.getState().lastError?.message ?? 'Printer not ready after reconnect'
        );
      }
    } catch (err) {
      setReconnectResult('fail');
      setReconnectError(err instanceof Error ? err.message : String(err));
    } finally {
      setReconnecting(false);
    }
  };

  const handleRetry = async () => {
    if (!pendingRetry) return;
    setRetrying(true);
    setRetryResult(null);
    try {
      setRetryResult((await pendingRetry()) ? 'success' : 'fail');
    } catch {
      setRetryResult('fail');
    } finally {
      setRetrying(false);
    }
  };

  // ── Bluetooth off → block the whole flow with where-to-enable guidance ──────
  if (bluetoothState === 'radioOff' || bluetoothState === 'permissionOff') {
    return <BluetoothOffNotice reason={bluetoothState} onRecheck={recheckBluetooth} />;
  }

  // ── No saved printer: choose one first ──────────────────────────────────────
  if (!activePrinter) {
    return (
      <Stack spacing={1.5}>
        {bluetoothState === 'on' && <BluetoothOnChip />}
        <Typography variant="body2" color="text.secondary">
          Scan for nearby receipt printers, then tap one to connect.
        </Typography>
        <AvailablePrintersList onConnected={onConnected} />
      </Stack>
    );
  }

  // ── Saved printer: diagnose + reconnect ─────────────────────────────────────
  return (
    <Stack spacing={2}>
      {bluetoothState === 'on' && <BluetoothOnChip />}

      {/* Compact automatic checks from the printer itself */}
      <Box>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Printer checks
        </Typography>
        {status ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            <SdkChip label="Online" ok={status.online} />
            <SdkChip label="Paper" ok={!status.paperEmpty} />
            <SdkChip label="Cover" ok={!status.coverOpen} />
            <SdkChip label="Cutter" ok={!status.cutterError} />
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No status yet — tap Reconnect Printer to try again.
          </Typography>
        )}
      </Box>

      {/* Reconnect + result */}
      <Stack spacing={1} alignItems="center">
        {reconnecting && (
          <Stack alignItems="center" spacing={1} sx={{ py: 0.5 }}>
            <CircularProgress size={26} />
            <Typography variant="body2" color="text.secondary">
              {reconnectStep ?? 'Reconnecting…'}
            </Typography>
          </Stack>
        )}

        {!reconnecting && reconnectResult === 'success' && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <MuiIcon icon={MuiCheckCircleIcon} width={22} sx={{ color: 'success.main' }} />
            <Typography variant="subtitle2" color="success.main">
              Printer reconnected
            </Typography>
          </Stack>
        )}

        {!reconnecting && reconnectResult === 'fail' && reconnectError && (
          <Typography variant="body2" color="error.main" textAlign="center">
            {reconnectError}
          </Typography>
        )}

        {reconnectResult !== 'success' && (
          <LoadingButton
            fullWidth
            variant="contained"
            size="large"
            loading={reconnecting}
            onClick={handleReconnect}
          >
            {reconnectResult === 'fail' ? 'Try Again' : 'Reconnect Printer'}
          </LoadingButton>
        )}

        {/* Retry the print that triggered troubleshooting, once reconnected. */}
        {reconnectResult === 'success' && pendingRetry && retryResult !== 'success' && (
          <LoadingButton
            variant="outlined"
            loading={retrying}
            onClick={handleRetry}
            sx={{ minWidth: 180 }}
          >
            {retryResult === 'fail' ? 'Retry Print Again' : 'Retry Print'}
          </LoadingButton>
        )}
        {reconnectResult === 'success' && retryResult === 'success' && (
          <Stack direction="row" alignItems="center" spacing={1}>
            <MuiIcon icon={MuiCheckCircleIcon} width={20} sx={{ color: 'success.main' }} />
            <Typography variant="body2" color="success.main">
              Print sent
            </Typography>
          </Stack>
        )}
      </Stack>

      {/* iOS guidance, tucked away: how to turn Bluetooth on and allow it when prompted. */}
      {Capacitor.getPlatform() === 'ios' && (
        <Box>
          <Button
            size="small"
            color="inherit"
            onClick={bluetoothHelp.onToggle}
            endIcon={
              <MuiIcon
                icon={bluetoothHelp.value ? MuiExpandLessIcon : MuiExpandMoreIcon}
              />
            }
          >
            How to turn on Bluetooth
          </Button>
          <Collapse in={bluetoothHelp.value} unmountOnExit>
            <Stack spacing={1.5} alignItems="center" sx={{ pt: 1.5 }}>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  To turn on Bluetooth: open Control Center and tap the Bluetooth icon.
                </Typography>
                <BluetoothControlCenterImage maxWidth={220} />
              </Stack>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="caption" color="text.secondary" textAlign="center">
                  If asked, tap Allow so PointCraft can use Bluetooth.
                </Typography>
                <Box
                  component="img"
                  src={allowConnectionsImg}
                  alt="Allow Bluetooth for new connections"
                  sx={{ width: '100%', maxWidth: 220, borderRadius: 2, boxShadow: 3 }}
                />
              </Stack>
            </Stack>
          </Collapse>
        </Box>
      )}

      <Divider />

      {/* Escape hatch: connect a different printer entirely. */}
      <Box>
        <Button
          size="small"
          color="inherit"
          onClick={switchPrinter.onToggle}
          endIcon={
            <MuiIcon icon={switchPrinter.value ? MuiExpandLessIcon : MuiExpandMoreIcon} />
          }
        >
          Use a different printer
        </Button>
        <Collapse in={switchPrinter.value} unmountOnExit>
          <Box sx={{ pt: 1.5 }}>
            <AvailablePrintersList onConnected={onConnected} />
          </Box>
        </Collapse>
      </Box>
    </Stack>
  );
}
